import {
  ChangeDetectionStrategy,
  Component,
  contentChild,
  inject,
  OnDestroy,
  TemplateRef,
  ViewContainerRef,
  EmbeddedViewRef,
  viewChild,
  computed,
  OnInit,
  input,
} from '@angular/core';
import { CommonModule, NgTemplateOutlet } from '@angular/common';
import { StadiaMapsContextService } from '../stadia-maps-context.service';
import type * as L from 'leaflet';
import { StadiaMapsMarkerService } from '../stadia-maps-marker/stadia-maps-marker.service';
import { StadiaMapsPopupService } from '../stadia-maps-popup.service';
import { StadiaMapsLoaderService } from '../stadia-maps-loader.service';
import { IPopupComponent } from '../stadia-maps.types';
import { IconCloseComponent } from '@pedix-workspace/angular-ui-icons';

@Component({
  selector: 'pxw-stadia-maps-popup',
  templateUrl: './stadia-maps-popup.component.html',
  styleUrls: ['./stadia-maps-popup.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, NgTemplateOutlet, IconCloseComponent],
})
export class StadiaMapsPopupComponent implements OnInit, OnDestroy, IPopupComponent {
  private stadiaMapsContext = inject(StadiaMapsContextService);
  private viewContainerRef = inject(ViewContainerRef);
  private stadiaMapsMarkerService = inject(StadiaMapsMarkerService);
  private stadiaMapsPopupService = inject(StadiaMapsPopupService);
  private stadiaMapsLoaderService = inject(StadiaMapsLoaderService);

  readonly popupTemplateRef = viewChild.required<TemplateRef<unknown>>('popupTemplate');
  readonly popupContentTemplate = contentChild.required<TemplateRef<unknown>>('popupContent', {
    descendants: true,
  });

  readonly displayClose = input<boolean>(false);
  readonly maxWidth = input<string>('240px');

  readonly markerInstance = computed(() => this.stadiaMapsMarkerService.getMarker());

  protected popupInstance: L.Popup | null = null;
  protected popupViewRef: EmbeddedViewRef<unknown> | null = null;
  protected L!: typeof import('leaflet');

  async ngOnInit(): Promise<void> {
    this.L = await this.stadiaMapsLoaderService.getLeaflet();
    const markerInstance = this.markerInstance();

    if (markerInstance) {
      markerInstance.on('click', (e: L.LeafletMouseEvent) => {
        this.L.DomEvent.stopPropagation(e.originalEvent);
        this.showPopup();
      });
      if (markerInstance.options.draggable) {
        markerInstance.on('dragstart', () => {
          this.hidePopup();
        });
      }
    }
  }

  ngOnDestroy(): void {
    this.cleanup();
  }

  showPopup(): void {
    this.stadiaMapsPopupService.removeAllPopupComponents();

    this.createPopup();

    this.stadiaMapsPopupService.registerPopupComponent(this);
  }
  hidePopup(): void {
    this.cleanup();
  }
  togglePopup() {
    if (this.popupInstance) {
      this.hidePopup();
    } else {
      this.showPopup();
    }
  }

  private createPopup() {
    const popupTemplate = this.popupTemplateRef();
    const markerInstance = this.markerInstance();

    if (!markerInstance) {
      return;
    }

    this.popupViewRef = this.viewContainerRef.createEmbeddedView(popupTemplate);

    const popupContainer = this.popupViewRef.rootNodes.find(
      node => node.nodeType === Node.ELEMENT_NODE,
    ) as HTMLElement;

    const markerElement = markerInstance.getElement();
    const markerHeight = markerElement
      ? markerElement.offsetHeight || markerElement.clientHeight || 0
      : 0;

    const maxWidthPx = this.resolveMaxWidth();

    this.popupInstance = this.L.popup({
      closeButton: false,
      closeOnClick: false,
      autoClose: false,
      autoPan: true,
      autoPanPadding: [25, 70],
      offset: [0, -markerHeight],
      maxWidth: maxWidthPx,
      className: 'stadia-maps__popup',
    })
      .setLatLng(markerInstance.getLatLng())
      .setContent(popupContainer);

    this.popupInstance.openOn(this.stadiaMapsContext.mapInstance);

    // Leaflet mide offsetWidth sincronicamente en openOn(), pero el contenido
    // Angular aun no esta renderizado → width: 51px (minWidth default).
    // Esperamos al siguiente frame para que Angular renderice, y forzamos
    // a Leaflet a re-medir con el contenido real.
    requestAnimationFrame(() => {
      this.popupInstance?.update();
    });
  }

  private resolveMaxWidth(): number {
    const maxWidth = this.maxWidth();

    if (maxWidth.includes('%')) {
      const percentage = parseInt(maxWidth, 10) / 100;
      const mapContainer = this.stadiaMapsContext.mapInstance.getContainer();
      return Math.floor(mapContainer.clientWidth * percentage);
    }

    return parseInt(maxWidth, 10) || 240;
  }

  private cleanup() {
    if (this.popupInstance) {
      this.popupInstance.remove();
      this.popupInstance = null;
    }
    if (this.popupViewRef) {
      this.popupViewRef.destroy();
      this.popupViewRef = null;
    }
    this.stadiaMapsPopupService.unregisterPopupComponent(this);
  }
}
